import React, { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { FileEdit, Plus } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { fmtDate, fmtMoney, INVOICE_STATUS, PO_STATUS, RECEIPT_STATUS } from "../../lib/labels";
import { userName, useUsers } from "../../lib/hooks";
import { Chip, EmptyState, PageHeader } from "../../components/common";
import { ApprovalCard } from "../../components/panels";
import { DemoBanner, FinActions, Money } from "../../components/finance";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

function ChangeDialog({ po, open, onClose, onDone }) {
  const [form, setForm] = useState({ type: "aumento", new_amount: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/finanzas/purchase-orders/${po.id}/changes`, { ...form, new_amount: Number(form.new_amount) });
      toast.success(r.data.status === "aplicada" ? "Modificación aplicada" : "Modificación enviada a aprobación");
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="po-change-dialog">
        <DialogHeader><DialogTitle>Modificar orden {po.po_number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Tipo</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="po-change-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="aumento">Aumento</SelectItem>
                <SelectItem value="reduccion">Reducción</SelectItem>
                <SelectItem value="alcance">Cambio de alcance</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Nuevo importe total (€)</Label><Input type="number" value={form.new_amount} onChange={(e) => setForm({ ...form, new_amount: e.target.value })} data-testid="po-change-amount" /></div>
          <div><Label>Motivo *</Label><Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="po-change-reason" /></div>
          <p className="text-xs text-slate-500">Diferencias superiores a 5.000 € requieren aprobación adicional antes de aplicarse.</p>
          <Button className="w-full" onClick={submit} disabled={busy || !form.reason.trim() || !Number(form.new_amount)} data-testid="po-change-submit">
            {busy ? "Registrando…" : "Registrar modificación"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function OrdenDetail({ id }) {
  const qc = useQueryClient();
  const { map } = useUsers();
  const [changeOpen, setChangeOpen] = useState(false);
  const { data, refetch } = useQuery({
    queryKey: ["fin-po", id],
    queryFn: async () => (await api.get(`/finanzas/purchase-orders/${id}`)).data,
  });
  if (!data) return null;
  const { purchase_order: po, actions, changes, receipts, invoices, exceptions, approvals, purchase_request } = data;

  const act = async (action, payload) => {
    await api.post(`/finanzas/purchase-orders/${po.id}/action`, { action, ...payload });
    toast.success("Acción ejecutada y registrada");
    refetch();
    qc.invalidateQueries({ queryKey: ["fin-pos"] });
  };

  return (
    <div data-testid="orden-detail">
      <PageHeader title={`${po.po_number} — ${po.supplier_name}`} subtitle={`${po.project || "Sin proyecto"} · Responsable: ${userName(map, po.owner_id)} · Entrega: ${fmtDate(po.delivery_date)}`} />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Chip map={PO_STATUS} value={po.status} testid="po-detail-status" />
        {purchase_request && <Link to={`/finanzas/solicitudes/${purchase_request.id}`} className="ts-chip ts-chip-info" data-testid="po-pr-link">Solicitud {purchase_request.pr_number} →</Link>}
        <span className="ts-chip ts-chip-muted">Partida {po.budget_reference || "—"}</span>
      </div>
      <DemoBanner />

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <div className="ts-card p-5" data-testid="po-summary">
            <div className="grid gap-3 sm:grid-cols-3">
              <div><p className="ts-caption">Total</p><Money value={po.total} className="text-xl font-bold text-slate-900" /></div>
              <div><p className="ts-caption">Aprobado</p><Money value={po.approved_amount} className="text-sm" /></div>
              <div><p className="ts-caption">Condiciones</p><p className="text-sm">{po.payment_terms}</p></div>
            </div>
            <Table>
              <TableHeader><TableRow><TableHead>Línea</TableHead><TableHead className="text-right">Cantidad</TableHead><TableHead className="text-right">Precio</TableHead><TableHead className="text-right">Importe</TableHead></TableRow></TableHeader>
              <TableBody>
                {(po.lines || []).map((l, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-sm">{l.description}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{l.quantity}</TableCell>
                    <TableCell className="text-right"><Money value={l.unit_price} /></TableCell>
                    <TableCell className="text-right"><Money value={l.amount} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="ts-card overflow-hidden" data-testid="po-receipts">
            <p className="border-b border-slate-100 px-5 py-3 font-display text-sm font-semibold">Recepciones ({receipts.length})</p>
            <Table>
              <TableHeader><TableRow><TableHead>Recepción</TableHead><TableHead>%</TableHead><TableHead>Estado</TableHead><TableHead>Retenido</TableHead><TableHead>Conformidad</TableHead></TableRow></TableHeader>
              <TableBody>
                {receipts.length === 0 && <TableRow><TableCell colSpan={5} className="py-5 text-center text-sm text-slate-500">Sin recepciones registradas.</TableCell></TableRow>}
                {receipts.map((r) => (
                  <TableRow key={r.id} data-testid={`po-receipt-${r.id}`}>
                    <TableCell className="font-mono text-xs">{r.receipt_number}</TableCell>
                    <TableCell className="text-xs">{r.received_pct}%</TableCell>
                    <TableCell><Chip map={RECEIPT_STATUS} value={r.status} /></TableCell>
                    <TableCell className="text-xs">{r.retained_amount ? fmtMoney(r.retained_amount) : "—"}</TableCell>
                    <TableCell className="text-xs">{r.accepted_by ? userName(map, r.accepted_by) : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="ts-card overflow-hidden" data-testid="po-invoices">
            <p className="border-b border-slate-100 px-5 py-3 font-display text-sm font-semibold">Facturas ({invoices.length})</p>
            <Table>
              <TableHeader><TableRow><TableHead>Factura</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
              <TableBody>
                {invoices.length === 0 && <TableRow><TableCell colSpan={3} className="py-5 text-center text-sm text-slate-500">Sin facturas sobre esta orden.</TableCell></TableRow>}
                {invoices.map((inv) => (
                  <TableRow key={inv.id} className="cursor-pointer" data-testid={`po-invoice-${inv.id}`}>
                    <TableCell><Link to={`/finanzas/facturas/${inv.id}`} className="font-mono text-xs text-blue-700 hover:underline">{inv.invoice_number}</Link></TableCell>
                    <TableCell><Chip map={INVOICE_STATUS} value={inv.status} /></TableCell>
                    <TableCell className="text-right"><Money value={inv.total} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {changes.length > 0 && (
            <div className="ts-card p-5" data-testid="po-changes">
              <p className="mb-2 font-display text-sm font-semibold">Modificaciones</p>
              {changes.map((c) => (
                <div key={c.id} className="mb-1.5 flex flex-wrap items-center gap-2 text-xs" data-testid={`po-change-${c.id}`}>
                  <span className="font-medium">{c.type}</span>
                  <Money value={c.previous_amount} /> → <Money value={c.new_amount} />
                  <span className={`ts-chip ${c.status === "aplicada" ? "ts-chip-success" : c.status === "rechazada" ? "ts-chip-danger" : "ts-chip-warning"}`}>{c.status}</span>
                  <span className="text-slate-500">{c.reason}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="ts-card p-5" data-testid="po-actions-card">
            <h3 className="mb-3 font-display text-sm font-semibold">Acciones controladas</h3>
            <FinActions actions={actions} onAct={act} testid="po-action" />
            {!["cancelada", "cerrada"].includes(po.status) && (
              <Button size="sm" variant="outline" className="mt-2" onClick={() => setChangeOpen(true)} data-testid="po-change-button">
                <FileEdit className="mr-1 h-3.5 w-3.5" /> Modificar orden
              </Button>
            )}
          </div>
          {exceptions.length > 0 && (
            <div className="ts-card p-5" data-testid="po-exceptions">
              <h3 className="mb-2 font-display text-sm font-semibold">Excepciones</h3>
              {exceptions.map((e) => (
                <div key={e.id} className="mb-2 rounded-sm border border-red-200 bg-red-50 p-2 text-xs">
                  <span className="font-semibold text-red-800">{e.type}</span> — {e.description}
                </div>
              ))}
            </div>
          )}
          {approvals.map((a) => <ApprovalCard key={a.id} appr={a} me={null} onChanged={refetch} />)}
        </div>
      </div>
      <ChangeDialog po={po} open={changeOpen} onClose={() => setChangeOpen(false)} onDone={() => { setChangeOpen(false); refetch(); }} />
    </div>
  );
}

export default function Ordenes() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState("");
  const { data: items } = useQuery({
    queryKey: ["fin-pos", status],
    queryFn: async () => (await api.get("/finanzas/purchase-orders", { params: { status: status || undefined } })).data,
  });

  if (id) return <OrdenDetail id={id} />;
  const highlight = params.get("po");

  return (
    <div data-testid="ordenes-page">
      <PageHeader title="Órdenes de compra" subtitle="Emisión controlada: documento generado, guards de proveedor/oferta/presupuesto y confirmación humana." />
      <div className="mb-4">
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-56" data-testid="po-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(PO_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {items && items.length === 0 && <EmptyState title="Sin órdenes" description="Las órdenes se crean al convertir solicitudes aprobadas." />}
      {items?.length > 0 && (
        <div className="ts-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow><TableHead>Orden</TableHead><TableHead>Proveedor</TableHead><TableHead>Proyecto</TableHead><TableHead>Entrega</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Total</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {items.map((po) => (
                <TableRow key={po.id} data-testid={`po-row-${po.id}`} className={`cursor-pointer ${highlight === po.id ? "bg-blue-50/60" : ""}`} onClick={() => navigate(`/finanzas/ordenes/${po.id}`)}>
                  <TableCell className="font-mono text-xs font-medium">{po.po_number}</TableCell>
                  <TableCell className="text-sm">{po.supplier_name}</TableCell>
                  <TableCell className="text-xs">{po.project || "—"}</TableCell>
                  <TableCell className="text-xs">{fmtDate(po.delivery_date)}</TableCell>
                  <TableCell><Chip map={PO_STATUS} value={po.status} testid={`po-status-${po.id}`} /></TableCell>
                  <TableCell className="text-right font-mono text-sm">{fmtMoney(po.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {highlight && <Link to={`/finanzas/ordenes/${highlight}`} className="mt-3 inline-block text-sm font-medium text-blue-700 hover:underline" data-testid="po-highlight-link">Abrir la orden del escenario →</Link>}
    </div>
  );
}
